"use client";

import { ScanEye, Wallet } from "lucide-react";
import { useTranslations } from "next-intl";
import { ImpersonationPanel } from "@/components/features/auth/ImpersonationPanel";
import { LoginForm } from "@/components/features/auth/LoginForm";
import { FundWalletPanel } from "@/components/features/dev/FundWalletPanel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/context/auth";
import { useWallet } from "@/context/wallet";
import { NET_MODE } from "@/contract/addresses";

export const TopNavLogin = () => {
  const t = useTranslations("auth");
  const tm = useTranslations("modals");
  const {
    isLoginDialogOpen,
    setIsLoginDialogOpen,
    logout,
    isLoggedIn,
    user,
    isImpersonating,
  } = useAuth();
  const { disconnect } = useWallet();

  const handleCancel = () => {
    setIsLoginDialogOpen(false);
    disconnect();
    logout();
  };

  // Both DEV and ADMIN roles can impersonate. Wallet login assigns ADMIN (from ADMIN_WALLETS),
  // while devLogin assigns DEV. Backend accepts both: @Roles(DEV, ADMIN).
  const userRole: string = user?.role ?? "";
  const canImpersonate =
    isLoggedIn &&
    (userRole === "DEV" || userRole === "ADMIN") &&
    !isImpersonating;

  return (
    <>
      <Dialog open={isLoginDialogOpen} onOpenChange={setIsLoginDialogOpen}>
        <DialogContent className="max-w-xs space-y-4 px-9">
          <DialogHeader className="pt-5 gap-2">
            <DialogTitle className="text-center">
              {tm("loginRequired.welcomeTitle")}
            </DialogTitle>
            <DialogDescription className="text-center">
              {tm("loginRequired.chooseUsername")}
            </DialogDescription>
          </DialogHeader>

          <LoginForm onCancel={handleCancel} />
        </DialogContent>
      </Dialog>

      {canImpersonate && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="p-1.5 rounded-md border border-semantic-alert/40 text-semantic-alert hover:bg-semantic-alert/10 transition-colors"
              aria-label={t("impersonation.impersonateAria")}
            >
              <ScanEye className="size-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" dir="rtl">
            <ImpersonationPanel />
          </PopoverContent>
        </Popover>
      )}

      {/* Local fork only -- no role/auth condition, unlike impersonation
          above. Whether this can even render is the only gate that matters:
          "is this the dev machine talking to anvil". */}
      {NET_MODE === "FORK" && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="p-1.5 rounded-md border border-semantic-success/40 text-semantic-success hover:bg-semantic-success/10 transition-colors"
              aria-label="Fund wallet on the local fork"
            >
              <Wallet className="size-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" dir="rtl">
            <FundWalletPanel />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};
